import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";

import { GlassPanel, Pill, Surface } from "@/components/glass";
import { NewProjectDialog } from "@/components/new-project-dialog";
import { useSession } from "@/lib/auth-client";

interface Project {
	id: string;
	name: string;
	organizationId?: string | null;
}

interface OnboardingProbe {
	user: { needsOnboarding: boolean };
	session: { status: string };
}

async function fetchProjects(): Promise<Project[]> {
	const r = await fetch("/api/projects", { credentials: "include" });
	if (!r.ok) throw new Error(`projects: ${r.status}`);
	const body = (await r.json()) as { projects: Project[] };
	return body.projects;
}

async function fetchOnboardingProbe(): Promise<OnboardingProbe> {
	const r = await fetch("/api/onboarding/session", { credentials: "include" });
	if (!r.ok) throw new Error(`onboarding: ${r.status}`);
	return r.json();
}

export default function Index() {
	const session = useSession();
	const navigate = useNavigate();
	const [newOpen, setNewOpen] = useState(false);

	// Spec 27: check whether the signed-in user still needs
	// onboarding before deciding where to send them. /welcome handles
	// the actual surface; this is just the routing decision.
	const onboardingQ = useQuery({
		queryKey: ["onboarding-probe"],
		queryFn: fetchOnboardingProbe,
		enabled: !!session.data?.user,
	});

	const needsOnboarding =
		onboardingQ.data?.user.needsOnboarding === true &&
		onboardingQ.data.session.status === "active";

	const projectsQ = useQuery({
		queryKey: ["projects"],
		queryFn: fetchProjects,
		// Defer the projects fetch until we've decided we're not
		// routing this user to /welcome. Avoids a brief render of the
		// "No projects yet" state for a brand-new signup.
		enabled: !!session.data?.user && onboardingQ.isSuccess && !needsOnboarding,
	});
	useEffect(() => {
		if (!projectsQ.data || projectsQ.data.length === 0) return;
		const allProjects = projectsQ.data;
		const activeOrganizationId =
			(session.data?.session as { activeOrganizationId?: string | null } | null)
				?.activeOrganizationId ?? null;
		// Favorites are surfaced in the project picker, not as a startup
		// redirect. Login lands on the active workspace's first project,
		// falling back to whatever project exists.
		const target =
			(activeOrganizationId
				? allProjects.find((project) => project.organizationId === activeOrganizationId)
				: undefined) ?? allProjects[0];
		if (!target) return;
		navigate(`/projects/${target.id}/graph`, { replace: true });
	}, [navigate, projectsQ.data, session.data?.session]);

	if (session.isPending) return null;
	if (!session.data?.user) return <Navigate to="/login" replace />;

	if (onboardingQ.isPending) return null;
	if (needsOnboarding) return <Navigate to="/welcome" replace />;

	if (projectsQ.isSuccess && projectsQ.data.length === 0) {
		return (
			<Surface className="flex items-center justify-center min-h-screen p-4">
				<GlassPanel className="p-8 w-full max-w-md space-y-5 text-center">
					<div className="space-y-2">
						<h1 className="text-[15px] font-medium tracking-tight">
							No projects yet
						</h1>
						<p className="text-[13px] text-[var(--color-text-muted)]">
							Create one to start ingesting memory.
						</p>
					</div>
					<div className="flex justify-center">
						<Pill onClick={() => setNewOpen(true)}>
							<Plus className="w-3.5 h-3.5" />
							New project
						</Pill>
					</div>
					<div className="pt-2 text-[11px] text-[var(--color-text-dim)]">
						<div>Or from the CLI:</div>
						<div className="mono mt-1">v3 project create --name &lt;name&gt;</div>
					</div>
				</GlassPanel>
				<NewProjectDialog open={newOpen} onOpenChange={setNewOpen} />
			</Surface>
		);
	}

	return null;
}
